import { useEffect, useRef, useState } from 'react';
import { Box, Grid2 as Grid, Stack, Typography } from '@mui/material';
import '../styles/Activities.css';
import 'animate.css';
import { useInView } from 'react-intersection-observer';

const items = [
	{
		img: 'https://thehighpoint.s3.ca-central-1.amazonaws.com/aerial-image-quadra-island-british-columbia.jpg',
		title: 'Wild Coastal Landscapes',
		boldContext:
			'Rocky shorelines, quiet beaches, and moss-lined forest trails — Quadra Island gives you space to breathe.',
		context:
			"Spot bald eagles gliding above as you hike the trails at Rebecca Spit or watch waves crash gently into driftwood-strewn coves. Here, nature isn't a backdrop — it's part of the rhythm of each day.",
	},
	{
		img: 'https://thehighpoint.s3.ca-central-1.amazonaws.com/cape-mudge/cape-mudge-lighthouse-19.png',
		title: 'Island Adventures & Tours',
		boldContext:
			'Cruise alongside humpbacks, spot grizzly bears on springtime tours, or cast a line on a guided fishing charter.',
		context:
			"Whether you're paddling through quiet inlets or joining a wildlife excursion to explore Discovery Passage, Quadra Island offers a front-row seat to the wild. With options for all experience levels — from first-timers to seasoned adventurers — your next unforgettable outing starts here.",
	},
	{
		img: 'https://thehighpoint.s3.ca-central-1.amazonaws.com/activities-3-1.jpg',
		title: 'Island Culture & Local Makers',
		boldContext:
			"Quadra's creative energy flows from ancient First Nations traditions to modern island makers.",
		context:
			"At the Nuyumbalees Cultural Centre, you'll find ceremonial masks, potlatch regalia, and carved artifacts — many tied to practices that span centuries, with local petroglyphs dating back over 2,500 years. Beyond that, explore artisan studios, weekend community markets, and seasonal art tours that invite you to connect with painters, potters, and storytellers from across the island.",
	},
	{
		img: 'https://thehighpoint.s3.ca-central-1.amazonaws.com/orange-sunrise-over-quadra-island.jpg',
		title: 'Island Time Starts Here',
		boldContext: 'Slow mornings, salty air, and space to breathe.',
		context:
			"Just 200 meters from the ferry, High Point Resort offers a peaceful harbourfront setting with calming views of Quathiaski Cove. Sip coffee as the ferry glides in below, unwind with a good book, or simply watch the light change across the water. Whether you're arriving or recharging, this is where you settle into island time.",
	},
];

export default function Activities() {
	const stackRefs = useRef([]);
	const imgRefs = useRef([]);
	const textRefs = useRef([]);
	const [visibleItems, setVisibleItems] = useState({});

	const { ref: titleRef, inView: titleInView } = useInView({
		threshold: 0.1,
		triggerOnce: true,
	});
	
	const { ref: otaLogosRef, inView: otaLogosInView } = useInView({
		threshold: 0.1,
		triggerOnce: true,
	});

	useEffect(() => {
		const observers = [];

		// Create observers for each item
		items.forEach((_, idx) => {
			// Observer for image
			if (imgRefs.current[idx]) {
				const imgObserver = new IntersectionObserver(
					([entry]) => {
						if (entry.isIntersecting) {
							setVisibleItems((prev) => ({
								...prev,
								[`img-${idx}`]: true,
							}));
							imgObserver.disconnect();
						}
					},
					{ threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
				);

				imgObserver.observe(imgRefs.current[idx]);
				observers.push(imgObserver);
			}

			// Observer for text
			if (textRefs.current[idx]) {
				const textObserver = new IntersectionObserver(
					([entry]) => {
						if (entry.isIntersecting) {
							setVisibleItems((prev) => ({
								...prev,
								[`text-${idx}`]: true,
							}));
							textObserver.disconnect();
						}
					},
					{ threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
				);

				textObserver.observe(textRefs.current[idx]);
				observers.push(textObserver);
			}
		});

		return () => {
			observers.forEach((observer) => observer.disconnect());
		};
	}, [items]);

	return (
		<Box
			id="activities"
			sx={{
				width: '100%',
				py: 4,
			}}
		>
			<Typography
				ref={titleRef}
				className={`title-container ${titleInView ? 'animate__animated animate__fadeInUp' : ''}`}
				variant="h3"
				fontWeight="bold"
				align="center"
				gutterBottom
				sx={{
					mt: { xs: 0, sm: 1, md: 2 },
					mb: { xs: 2.5, sm: 7.5, md: 10 },
				}}
			>
				Drift In, Breathe Out
			</Typography>
			{items.map((item, idx) => (
				<Stack
					key={idx}
					ref={(el) => (stackRefs.current[idx] = el)}
					spacing={2}
					alignItems="center"
					gutterBottom
					sx={{
						my: 0,
						gap: { xs: 0, md: 5, lg: 10 },
					}}
				>
					<Grid
						className={`grid-container-${idx}`}
						container
						sx={{
							justifyContent: 'space-between',
							flexDirection: {
								sm: idx % 2 === 0 ? 'row' : 'row-reverse',
							},
						}}
					>
						<Grid
							ref={(el) => (imgRefs.current[idx] = el)}
							className={`img-container ${visibleItems[`img-${idx}`] ? 'animate__animated animate__fadeInUp' : ''}`}
							size={6}
						>
							<Box
								component="img"
								src={item.img}
								alt={item.title}
							/>
						</Grid>
						<Grid
							ref={(el) => (textRefs.current[idx] = el)}
							className={`text-container ${visibleItems[`text-${idx}`] ? 'animate__animated animate__fadeInUp' : ''}`}
							size={5}
							sx={{
								mr: {
									sm: idx % 2 == 0 ? 2 : 0,
									md: idx % 2 == 0 ? 4 : 0,
									lg: idx % 2 == 0 ? 8 : 0,
									xl: idx % 2 == 0 ? 12 : 0,
								},
								ml: {
									sm: idx % 2 == 0 ? 0 : 2,
									md: idx % 2 == 0 ? 0 : 4,
									lg: idx % 2 == 0 ? 0 : 8,
									xl: idx % 2 == 0 ? 0 : 12,
								},
								textAlign: idx % 2 == 0 ? 'right' : 'left',
							}}
						>
							<Typography
								variant="h5"
								fontWeight="bold"
								gutterBottom
							>
								{item.title}
							</Typography>
							<Typography
								variant="body1"
								gutterBottom
								sx={{
									justifySelf:
										idx % 2 == 0 ? 'right' : 'left',
								}}
							>
								<b>{item.boldContext}</b> {item.context}
							</Typography>
						</Grid>
					</Grid>
				</Stack>
			))}
			<Grid
				ref={otaLogosRef}
				className={`ota-logos-activities ${otaLogosInView ? 'animate__animated animate__fadeInUp' : ''}`}
			>
				<a
					href="https://live.ipms247.com/booking/book-rooms-426634"
					className="book-now-in-activities"
					target="_blank"
					rel="noopener noreferrer"
				>
					BOOK NOW
				</a>
			</Grid>
		</Box>
	);
}
